import { RestError } from '@azure/core-http';
import { CarrierDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useForm } from 'react-hook-form';
import { castDraft } from 'immer';
import { useImmer } from 'use-immer';
import { useMutationEditCarrier } from '../../../carriers.queries';
import { CarrierFormState } from '../carrier-form-fields.component';
import { hasEditableSortOrder, sortOrderToSend } from '../../../logic/carrier-sort-order';

export interface EditCarrierActionProps {
  carrier: CarrierDto;
  onAbort(): void;
  onSuccess(): void;
}

export function useEditCarrierAction({ carrier, onAbort, onSuccess }: EditCarrierActionProps) {
  const [state, setState] = useImmer<{ saving: boolean; error?: RestError | Error }>({ saving: false });

  const { control, handleSubmit, setValue, watch } = useForm<CarrierFormState>({
    defaultValues: {
      name: carrier.name,
      shortName: carrier.shortName ?? '',
      image: carrier.image ?? '',
      searchPriority: carrier.searchPriority === undefined ? '' : String(carrier.searchPriority)
    }
  });
  const isSortOrderEditable = hasEditableSortOrder(carrier);

  const editMutation = useMutationEditCarrier(carrier.id);

  const handlers = {
    onCancel: onAbort,
    onSubmit(form: CarrierFormState) {
      setState((draft) => {
        draft.saving = true;
      });

      editMutation.mutate(
        // A cleared short name is sent as empty rather than dropped, so it can be removed. The sort
        // order is sent only when changed, so other edits leave it alone.
        {
          name: form.name.trim(),
          shortName: form.shortName.trim(),
          image: form.image || undefined,
          searchPriority: isSortOrderEditable ? sortOrderToSend(form.searchPriority, carrier.searchPriority) : undefined
        },
        {
          onSuccess() {
            onSuccess();
          },
          onError(error) {
            setState((draft) => {
              draft.saving = false;
              draft.error = castDraft(error);
            });
          }
        }
      );
    }
  };

  return {
    state,
    handlers,
    control,
    handleSubmit,
    image: watch('image'),
    sortOrder: isSortOrderEditable ? { isSet: carrier.searchPriority !== undefined } : undefined,
    onImageUploaded: (url: string) => setValue('image', url, { shouldDirty: true })
  } as const;
}
